import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { getProjectWorkRoomByProjectId } from "../../../core/services/projectWorkRoom.service";
import { mobileNavMediaQuery } from "../../../layout/layoutMetrics";
import FinalBriefModal from "./FinalBriefModal";
import { useAuth } from "../../../app/context/AuthContext";
import { MODULE_LIST } from "../../../constants/commons";
import { WORKROOM_STEP, dispatchCreateWorkroom } from "../../../layout/workroomEditEvents";

function unwrapWorkroom(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload.find(Boolean) ?? null;
  if (payload?.data && typeof payload.data === "object") return payload.data;
  return payload;
}

function StrategyField({ label, children }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography
        component="div"
        sx={{
          fontSize: 12.5,
          fontWeight: 800,
          color: "rgba(255,255,255,0.95)",
          mb: 0.4,
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

const strategyBodySx = {
  fontSize: 10.5,
  fontWeight: 400,
  lineHeight: 1.5,
  color: "rgba(255,255,255,0.72)",
};

function parseNonNegotiables(value) {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.map(String).map((item) => item.trim()).filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parseNonNegotiables(parsed);
      } catch {
        // Fall through to delimiter parsing.
      }
    }

    return trimmed
      .split(/[\n,]+/)
      .map((item) => item.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean);
  }

  return [];
}

function StrategyList({ items }) {
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
      {items.map((item, index) => (
        <Box
          component="li"
          key={`${item}-${index}`}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            mb: 0.45,
            ...strategyBodySx,
          }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 4,
              height: 4,
              minWidth: 4,
              mt: "0.55em",
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.55)",
            }}
          />
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            {item}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function WorkroomStrategyPanel({
  projectId,
  clientId,
  onEditStrategies,
  refreshKey = 0,
  allowShrink = false,
}) {
  const [workroom, setWorkroom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [finalBriefOpen, setFinalBriefOpen] = useState(false);



  const { userPermissions } = useAuth()
      
  const workroomModule = Object.hasOwn(userPermissions, MODULE_LIST.WORKROOM) ? userPermissions[MODULE_LIST.WORKROOM] : false;

  let canViewWorkroom = false;
  let canEditWorkroom = false;
  if (workroomModule && (workroomModule.edit || workroomModule.view)) {
    canViewWorkroom = true;
    canEditWorkroom = workroomModule.edit;
  }

  const canOpenFinalBrief =
    clientId != null &&
    String(clientId).trim() !== "" &&
    projectId != null &&
    String(projectId).trim() !== "";

  useEffect(() => {
    if (projectId == null || String(projectId).trim() === "") {
      setWorkroom(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadWorkroom = async () => {
      setLoading(true);
      try {
        const response = await getProjectWorkRoomByProjectId(projectId);
        if (!cancelled) setWorkroom(unwrapWorkroom(response));
      } catch {
        if (!cancelled) setWorkroom(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadWorkroom();

    return () => {
      cancelled = true;
    };
  }, [projectId, refreshKey]);

  const objective = workroom?.objective ?? workroom?.Objective ?? "";
  const audience = workroom?.audience ?? workroom?.Audience ?? "";
  const message = workroom?.message ?? workroom?.Message ?? "";
  const nonNegotiables = parseNonNegotiables(
    workroom?.nonNegotiables ??
      workroom?.NonNegotiables ??
      workroom?.nonNegotiable ??
      workroom?.NonNegotiable,
  );

  const strategyFields = [
    { label: "Objective:", content: objective },
    { label: "Audience:", content: audience },
    { label: "Message:", content: message },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        flex: allowShrink ? "1 1 auto" : "0 0 auto",
        alignSelf: allowShrink ? "stretch" : "flex-start",
        minHeight: 0,
        minWidth: 0,
        maxHeight: allowShrink ? "100%" : undefined,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: allowShrink ? "hidden" : "visible",
        pb: "28px",
      }}
    >
      <Box
        sx={{
          flex: allowShrink ? "1 1 auto" : "0 0 auto",
          height: allowShrink ? "100%" : 315,
          maxHeight: allowShrink ? 315 : undefined,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          borderRadius: "16px",
          background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          overflow: "hidden",
          [`@media ${mobileNavMediaQuery}`]: {
            ...(allowShrink
              ? { maxHeight: "min(260px, 42vh)" }
              : { height: "min(260px, 42vh)" }),
          },
        }}
      >
        <Box
          sx={{
            flex: "1 1 0%",
            minHeight: 0,
            overflowY: "auto",
            px: 2.25,
            pt: 1.5,
            pb: 1,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(255,255,255,0.22)",
              borderRadius: 10,
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 3,
              }}
            >
              <CircularProgress size={18} sx={{ color: "rgba(255,255,255,0.45)" }} />
            </Box>
          ) : (
            <>
              {strategyFields.map(({ label, content }) => (
                <StrategyField key={label} label={label}>
                  {content ? (
                    <Typography sx={strategyBodySx}>{content}</Typography>
                  ) : null}
                </StrategyField>
              ))}

              <StrategyField label="Non-Negotiables:">
                {nonNegotiables.length ? (
                  <StrategyList items={nonNegotiables} />
                ) : null}
              </StrategyField>
            </>
          )}
        </Box>

        <Box
          component="button"
          type="button"
          onClick={() => {
            if (workroom && onEditStrategies) {
              onEditStrategies(workroom);
              return;
            }
            // No workroom yet: open CreateWorkroomModal at the strategy/brief step.
            dispatchCreateWorkroom({ projectId, step: WORKROOM_STEP.NAPKIN_BRIEF });
          }}
          disabled={loading}
          sx={{
            flexShrink: 0,
            display: canEditWorkroom ? "flex" : "none",
            alignItems: "center",
            gap: 0.6,
            px: 2.25,
            py: 1,
            border: 0,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            background: "transparent",
            color: "rgba(255,255,255,0.42)",
            cursor: canEditWorkroom && !loading ? "pointer" : "default",
            width: "100%",
            textAlign: "left",
            "&:hover": canEditWorkroom && !loading
              ? { color: "rgba(255,255,255,0.72)" }
              : undefined,
            "&:disabled": {
              opacity: 0.45,
              cursor: "default",
            },
          }}
        >
          <EditNoteOutlinedIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: 11.5 }}>edit strategies</Typography>
        </Box>
      </Box>

      <Box
        component="button"
        type="button"
        onClick={() => setFinalBriefOpen(true)}
        disabled={!canOpenFinalBrief}
        aria-haspopup="dialog"
        aria-expanded={finalBriefOpen}
        sx={{
          position: "absolute",
          left: 3,
          bottom: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.4,
          width: "auto",
          px: 1,
          py: 0.35,
          borderRadius: "8px",
          border: "1px solid rgba(255,255,255,0.1)",
          background: "rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.7)",
          cursor: canOpenFinalBrief ? "pointer" : "default",
          lineHeight: 1,
          zIndex: 1,
          transition: "background 140ms ease, color 140ms ease",
          "&:hover": canOpenFinalBrief
            ? {
                background: "rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.92)",
              }
            : undefined,
          "&:disabled": {
            opacity: 0.4,
            cursor: "default",
          },
        }}
      >
        <DescriptionOutlinedIcon sx={{ fontSize: 12 }} />
        <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 600 }}>
          Final Brief
        </Typography>
      </Box>

      <FinalBriefModal
        open={finalBriefOpen}
        onClose={() => setFinalBriefOpen(false)}
        clientId={clientId}
        projectId={projectId}
      />
    </Box>
  );
}
